import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  adminFetchBtwApplicationById,
  adminArchiveBtwApplication,
  adminDeleteBtwApplication,
  adminFetchBtwStatuses,
  adminFetchAdmins,
  adminFetchBtwLabels,
  adminSetBtwApplicationLabels,
  adminUpdateBtwDeadline,
  adminUpdateBtwWorkflowStatus,
  adminUpdateBtwAssignee,
  adminAddBtwComment,
  adminEditBtwComment,
  adminDeleteBtwComment,
  adminUpdateBtwDetails,
  adminAddBtwFile,
  adminReplaceBtwInvoice,
  adminDeleteBtwInvoice,
  adminDeleteBtwOtherFile,
  adminDownloadBtwMachtigingPdf,
  adminDownloadBelastingdienstFormFile,
  adminConfirmBtwInvoicePaid,
} from "../../core/axios/endpoints";
import { useUser } from "../../core/context/user";
import ConfirmModal from "../../components/confirm-modal";
import {
  batteryUsageLabel,
  genderLabel,
  step2ValueLabel,
} from "../../core/btw-field-labels";
import SendCustomerEmailModal from "../../components/send-customer-email-modal";
import BtwStatusMenu from "../../components/btw-status-menu";
import BtwAssigneeMenu from "../../components/btw-assignee-menu";
import BtwActivityFeed from "../../components/btw-activity-feed";
import BtwLabelPicker from "../../components/btw-label-picker";
import BtwDeadlinePicker from "../../components/btw-deadline-picker";
import BelastingdienstFormModal from "../../components/belastingdienst-form-modal";
import BtwBerekeningSection from "../../components/btw-berekening-section";

const STATUS_LABELS = {
  pending_payment: { label: "Wacht op betaling", color: "bg-amber-100 text-amber-800" },
  paid: { label: "Betaald", color: "bg-emerald-100 text-emerald-800" },
  abandoned: { label: "Afgebroken", color: "bg-gray-100 text-gray-700" },
  refunded: { label: "Terugbetaald", color: "bg-rose-100 text-rose-800" },
};

// Voor partner-mode aanvragen heeft "Betaald" geen betekenis (klant betaalt
// nooit). Hier mappen we de payment-status om naar partner-context.
const PARTNER_PAYMENT_LABELS = {
  paid: { label: "Aanvraag ontvangen", color: "bg-violet-100 text-violet-800" },
};

const PARTNER_INVOICE_LABELS = {
  pending: { label: "Nog te factureren", color: "bg-slate-100 text-slate-700" },
  invoiced: { label: "Factuur verstuurd", color: "bg-sky-100 text-sky-800" },
  paid: { label: "Factuur betaald", color: "bg-emerald-100 text-emerald-800" },
  credited: { label: "Gecrediteerd", color: "bg-rose-100 text-rose-800" },
};

const QUAL_LABELS = {
  qualified: { label: "Geschikt", color: "bg-emerald-100 text-emerald-800" },
  disqualified: { label: "Handmatige beoordeling", color: "bg-amber-100 text-amber-800" },
  incomplete: { label: "Incompleet", color: "bg-gray-100 text-gray-700" },
};

const QUALIFICATION_FLAG_LABELS = {
  previous_vat_pre2020_not_sold:
    "Eerder BTW teruggevraagd vóór 2020 - oude woning nog niet verkocht",
  previous_vat_pre2020_no_move:
    "Eerder BTW teruggevraagd vóór 2020 - niet verhuisd",
  entrepreneur_complex_form:
    "Onderneming in complexe rechtsvorm (BV/VOF/maatschap)",
  entrepreneur_no_vat: "Ondernemer zonder BTW-aangifte",
  vat_over_kor_threshold:
    "BTW > € 2.495 - 4 jaar BTW-aangifte vereist (geen directe KOR)",
  vat_under_threshold:
    "BTW onder drempel (oude logica - aanvraag handmatig nakijken)",
};

const STEP2_LABELS = {
  previousVat: "Eerder BTW teruggevraagd",
  previousInstall: "Eerdere installatie",
  moved: "Verhuisd afgelopen jaar",
  oldHouseSold: "Oude woning verkocht",
  hasPartner: "Fiscale partner",
  partnerEntrepreneur: "Partner is BTW-ondernemer",
  partnerFutureEntrepreneur: "Partner wordt ondernemer",
  isEntrepreneur: "Klant is al BTW-ondernemer",
  businessForm: "Bedrijfsvorm",
  futureEntrepreneur: "Klant wordt ondernemer",
  vatThreshold: "Onder BTW-drempel (KOR)",
};

const Badge = ({ map, value }) => {
  const cfg = map[value] ?? { label: value || "-", color: "bg-gray-100 text-gray-700" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${cfg.color}`}
    >
      {cfg.label}
    </span>
  );
};

const yesNoLabel = (v) => {
  if (v === "ja") return "Ja";
  if (v === "nee") return "Nee";
  if (v === "na" || v === "n.v.t.") return "n.v.t.";
  if (v === undefined || v === null || v === "") return "-";
  return String(v);
};

const formatDate = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("nl-NL");
};

const formatDateTime = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("nl-NL");
};

const Row = ({ label, value, mono = false }) => (
  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 py-2.5 text-sm last:border-b-0">
    <dt className="col-span-1 text-gray-500">{label}</dt>
    <dd className={`col-span-2 text-gray-900 ${mono ? "font-mono text-xs break-all" : ""}`}>
      {value ?? "-"}
    </dd>
  </div>
);

const EditRow = ({ label, type = "text", value, onChange, options }) => (
  <div className="grid grid-cols-3 gap-4 border-b border-gray-100 py-2 text-sm last:border-b-0">
    <label className="col-span-1 pt-1.5 text-gray-500">{label}</label>
    {options ? (
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="col-span-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
      >
        <option value="">-</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    ) : (
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="col-span-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
      />
    )}
  </div>
);

const Section = ({
  title,
  children,
  editable = false,
  isEditing = false,
  saving = false,
  onEdit,
  onSave,
  onCancel,
}) => (
  <section className="mb-5 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      {editable ? (
        isEditing ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="rounded px-2 py-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50"
            >
              Annuleer
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? "Opslaan…" : "Opslaan"}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Bewerken
          </button>
        )
      ) : null}
    </div>
    <dl className="divide-y divide-gray-100">{children}</dl>
  </section>
);

const sortActivitiesDesc = (list = []) =>
  [...list].sort(
    (a, b) =>
      new Date(b.createdAt ?? 0).getTime() -
      new Date(a.createdAt ?? 0).getTime(),
  );

const BtwBatterijAanvraagDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromList = Boolean(location.state?.from);
  const { userData } = useUser();
  const [data, setData] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [allLabels, setAllLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const refresh = () => setReloadKey((k) => k + 1);
  const [deleting, setDeleting] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [editingSection, setEditingSection] = useState(null); // "step1" | "step3" | null
  const [editDraft, setEditDraft] = useState({});
  const [savingSection, setSavingSection] = useState(false);
  const [fileBusy, setFileBusy] = useState(null); // "invoice" | "addFile" | other-fileId
  const [downloadingMachtiging, setDownloadingMachtiging] = useState(false);
  const [invoicePaidBusy, setInvoicePaidBusy] = useState(false);

  const confirmInvoicePaid = () =>
    askConfirm({
      title: "Betaling bevestigen?",
      message:
        "De klant heeft doorgegeven dat de factuur aan de installateur volledig is betaald. De bevestigings-datum wordt het moment van nu.",
      confirmLabel: "Bevestig betaling",
      onConfirm: async () => {
        const res = await adminConfirmBtwInvoicePaid(id, { confirmed: true });
        setData(res?.data ?? res);
      },
    });

  const undoInvoicePaid = () =>
    askConfirm({
      title: "Betaal-bevestiging ongedaan maken?",
      message:
        "De bevestiging (en een eventueel geupload betaalbewijs) wordt verwijderd van deze aanvraag.",
      confirmLabel: "Ongedaan maken",
      variant: "danger",
      onConfirm: async () => {
        const res = await adminConfirmBtwInvoicePaid(id, { confirmed: false });
        setData(res?.data ?? res);
      },
    });

  const uploadInvoicePaidProof = async (file) => {
    if (!file) return;
    setInvoicePaidBusy(true);
    try {
      const res = await adminConfirmBtwInvoicePaid(id, {
        confirmed: true,
        file,
      });
      setData(res?.data ?? res);
    } catch (err) {
      showError(
        err?.response?.data?.error ?? "Uploaden van betaalbewijs mislukt.",
      );
    } finally {
      setInvoicePaidBusy(false);
    }
  };

  // Belastingdienst-formulier ON0202: openen gaat via een modal waarin de
  // waarden eerst gecontroleerd worden.
  const [belastingdienstOpen, setBelastingdienstOpen] = useState(false);
  const [belastingdienstBusy, setBelastingdienstBusy] = useState(false);

  const downloadBelastingdienstFormulier = async () => {
    setBelastingdienstBusy(true);
    try {
      const blob = await adminDownloadBelastingdienstFormFile(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        data?.belastingdienstForm?.file?.originalName ??
        "belastingdienst-formulier.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErrorBanner("Het opgeslagen formulier kon niet opgehaald worden.");
    } finally {
      setBelastingdienstBusy(false);
    }
  };

  const downloadMachtigingPdf = async () => {
    setDownloadingMachtiging(true);
    try {
      const blob = await adminDownloadBtwMachtigingPdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `machtiging-${data?.step1?.achternaam ?? id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErrorBanner("Download van machtiging-PDF mislukt.");
    } finally {
      setDownloadingMachtiging(false);
    }
  };
  // Generieke action-confirm modal + foutbanner (vervangt window.confirm/alert).
  const [actionModal, setActionModal] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [errorBanner, setErrorBanner] = useState(null);

  const askConfirm = (config) => setActionModal(config);
  const runConfirm = async () => {
    if (!actionModal?.onConfirm) return;
    setActionBusy(true);
    try {
      await actionModal.onConfirm();
      setActionModal(null);
    } catch (err) {
      setErrorBanner(
        err?.response?.data?.error ?? err?.message ?? "Actie mislukt.",
      );
    } finally {
      setActionBusy(false);
    }
  };
  const showError = (msg) => setErrorBanner(msg ?? "Er ging iets mis.");

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      adminFetchBtwApplicationById(id),
      adminFetchBtwStatuses(),
      adminFetchAdmins(),
      adminFetchBtwLabels(),
    ])
      .then(([appRes, statusRes, adminRes, labelRes]) => {
        setData(appRes?.data ?? appRes);
        setStatuses(statusRes?.data ?? statusRes ?? []);
        setAdmins(adminRes?.data ?? adminRes ?? []);
        setAllLabels(labelRes?.data ?? labelRes ?? []);
      })
      .catch((err) =>
        setError(err?.response?.data?.error ?? err.message ?? "Onbekende fout"),
      )
      .finally(() => setLoading(false));
  }, [id, reloadKey]);

  const activities = useMemo(
    () => sortActivitiesDesc(data?.activities ?? []),
    [data?.activities],
  );

  const toggleArchive = async () => {
    if (!data) return;
    const shouldArchive = !data.isArchived;
    setArchiving(true);
    try {
      const res = await adminArchiveBtwApplication(id, shouldArchive);
      setData(res?.data ?? res);
    } catch (err) {
      console.error("Archiveren mislukt", err);
      showError(err?.response?.data?.error ?? "Archiveren mislukt.");
    } finally {
      setArchiving(false);
      setConfirmOpen(false);
    }
  };

  const runDelete = async () => {
    if (!data) return;
    setDeleting(true);
    try {
      await adminDeleteBtwApplication(id);
      navigate("/panel/admin/btw-batterij-overzicht");
    } catch (err) {
      console.error("Verwijderen mislukt", err);
      showError(err?.response?.data?.error ?? "Verwijderen mislukt.");
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async (nextStatus, comment) => {
    try {
      const res = await adminUpdateBtwWorkflowStatus(id, nextStatus._id, comment);
      setData(res?.data ?? res);
    } catch (err) {
      showError(
        err?.response?.data?.error ?? err?.message ?? "Status wijzigen mislukt.",
      );
      throw err;
    }
  };

  const handleAssigneeChange = async (nextAdmin, comment) => {
    try {
      const res = await adminUpdateBtwAssignee(
        id,
        nextAdmin ? nextAdmin._id : null,
        comment,
      );
      setData(res?.data ?? res);
    } catch (err) {
      showError(
        err?.response?.data?.error ?? err?.message ?? "Toewijzing wijzigen mislukt.",
      );
      throw err;
    }
  };

  const handleDeadlineChange = async ({ deadlineAt, deadlineNote, reminderSchedule }) => {
    try {
      const res = await adminUpdateBtwDeadline(id, {
        deadlineAt,
        deadlineNote,
        reminderSchedule,
      });
      setData(res?.data ?? res);
    } catch (err) {
      showError(
        err?.response?.data?.error ?? err?.message ?? "Deadline bijwerken mislukt.",
      );
      throw err;
    }
  };

  const handleLabelsChange = async (nextIds) => {
    try {
      const res = await adminSetBtwApplicationLabels(id, nextIds);
      setData(res?.data ?? res);
    } catch (err) {
      showError(
        err?.response?.data?.error ?? err?.message ?? "Labels bijwerken mislukt.",
      );
      throw err;
    }
  };

  const handleAddComment = async (body) => {
    setPostingComment(true);
    try {
      const res = await adminAddBtwComment(id, body);
      const payload = res?.data ?? res;
      setData((prev) =>
        prev
          ? {
              ...prev,
              activities: [...(prev.activities ?? []), payload.activity],
              lastActivityAt: payload.lastActivityAt ?? prev.lastActivityAt,
            }
          : prev,
      );
    } finally {
      setPostingComment(false);
    }
  };

  const handleEditComment = async (commentId, body) => {
    const res = await adminEditBtwComment(id, commentId, body);
    const updated = res?.data ?? res;
    setData((prev) =>
      prev
        ? {
            ...prev,
            activities: (prev.activities ?? []).map((a) =>
              String(a._id) === String(commentId)
                ? { ...a, body: updated.body ?? body, updatedAt: updated.updatedAt, isEdited: true }
                : a,
            ),
          }
        : prev,
    );
  };

  const startEdit = (section, initial) => {
    setEditDraft({ ...initial });
    setEditingSection(section);
  };
  const cancelEdit = () => {
    setEditDraft({});
    setEditingSection(null);
  };
  const setDraftField = (key, value) =>
    setEditDraft((d) => ({ ...d, [key]: value }));

  const saveSection = async (section) => {
    setSavingSection(true);
    try {
      const res = await adminUpdateBtwDetails(id, { [section]: editDraft });
      setData(res?.data ?? res);
      // Berekening teruggave haalt zijn eigen data op; die moet mee-verversen
      // als hier het aankoopbedrag of de capaciteit is aangepast.
      setReloadKey((k) => k + 1);
      cancelEdit();
    } catch (err) {
      showError(err?.response?.data?.error ?? err?.message ?? "Opslaan mislukt.");
    } finally {
      setSavingSection(false);
    }
  };

  const handleReplaceInvoice = async (file) => {
    if (!file) return;
    setFileBusy("invoice");
    try {
      const res = await adminReplaceBtwInvoice(id, file);
      setData(res?.data ?? res);
    } catch (err) {
      showError(err?.response?.data?.error ?? err?.message ?? "Upload mislukt.");
    } finally {
      setFileBusy(null);
    }
  };

  const handleDeleteInvoice = () => {
    askConfirm({
      title: "Factuur verwijderen?",
      message: "De geüploade factuur wordt verwijderd uit deze aanvraag. Dit is niet ongedaan te maken.",
      variant: "danger",
      confirmLabel: "Verwijder factuur",
      onConfirm: async () => {
        setFileBusy("invoice");
        try {
          const res = await adminDeleteBtwInvoice(id);
          setData(res?.data ?? res);
        } finally {
          setFileBusy(null);
        }
      },
    });
  };

  const handleAddFile = async (file) => {
    if (!file) return;
    setFileBusy("addFile");
    try {
      const res = await adminAddBtwFile(id, file);
      setData(res?.data ?? res);
    } catch (err) {
      showError(err?.response?.data?.error ?? err?.message ?? "Upload mislukt.");
    } finally {
      setFileBusy(null);
    }
  };

  const handleDeleteOtherFile = (publicId) => {
    askConfirm({
      title: "Bijlage verwijderen?",
      message: "De bijlage wordt verwijderd. Dit is niet ongedaan te maken.",
      variant: "danger",
      confirmLabel: "Verwijder bijlage",
      onConfirm: async () => {
        setFileBusy(publicId);
        try {
          const res = await adminDeleteBtwOtherFile(id, publicId);
          setData(res?.data ?? res);
        } finally {
          setFileBusy(null);
        }
      },
    });
  };

  const handleDeleteComment = (commentId) => {
    askConfirm({
      title: "Opmerking verwijderen?",
      message: "Je opmerking wordt verwijderd uit de activity feed.",
      variant: "danger",
      confirmLabel: "Verwijder",
      onConfirm: async () => {
        await adminDeleteBtwComment(id, commentId);
        setData((prev) =>
          prev
            ? {
                ...prev,
                activities: (prev.activities ?? []).filter(
                  (a) => String(a._id) !== String(commentId),
                ),
              }
            : prev,
        );
      },
    });
  };

  if (loading) {
    return <div className="page-wrapper px-6 pt-24 pb-12">Laden...</div>;
  }
  if (error) {
    return <div className="page-wrapper px-6 pt-24 pb-12 text-rose-700">{error}</div>;
  }
  if (!data) {
    return <div className="page-wrapper px-6 pt-24 pb-12">Niet gevonden.</div>;
  }

  const { step1 = {}, step2 = {}, step3 = {} } = data;
  const fullName = `${step1.voorletters ?? ""} ${step1.tussenvoegsel ?? ""} ${step1.achternaam ?? ""}`
    .replace(/\s+/g, " ")
    .trim();
  const adres = step1.straatnaam
    ? `${step1.straatnaam} ${step1.huisnummer ?? ""}, ${step1.postcode ?? ""} ${step1.plaatsnaam ?? ""}`.trim()
    : null;

  const workflowStatus = data.workflowStatusId ?? null;
  const assignee = data.assignedToAdminId ?? null;

  return (
    <div className="page-wrapper px-6 pt-24 pb-12">
      {errorBanner ? (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <span>{errorBanner}</span>
          <button
            type="button"
            onClick={() => setErrorBanner(null)}
            className="text-rose-500 hover:text-rose-800"
            aria-label="Sluit foutmelding"
          >
            ✕
          </button>
        </div>
      ) : null}
      <div className="mb-4">
        <button
          type="button"
          onClick={() => {
            // Alleen browser-back als we via de lijst/board hier kwamen - die zet
            // `location.state.from`. Bij een deeplink/refresh ontbreekt state en
            // gaan we naar de overzicht-URL (zonder filter-context terug te knallen
            // naar een onbekende vorige pagina).
            if (cameFromList) navigate(-1);
            else navigate("/panel/admin/btw-batterij-overzicht");
          }}
          className="text-sm text-emerald-700 hover:underline"
        >
          ← Terug naar overzicht
        </button>
      </div>

      <div className="mb-6 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-2xl font-semibold text-gray-900">
              {fullName || "Onbekende aanvrager"}
              {data.isArchived ? (
                <span className="ml-3 inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 align-middle text-xs font-medium text-gray-700">
                  Gearchiveerd
                </span>
              ) : null}
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Aanvraag <span className="font-mono">{(data._id ?? "").slice(-8)}</span>
              {" · "}
              Ingediend {formatDateTime(data.createdAt)}
              {data.lastActivityAt ? (
                <>
                  {" · "}
                  Laatste activiteit {formatDateTime(data.lastActivityAt)}
                </>
              ) : null}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setEmailOpen(true)}
              disabled={archiving || deleting || !step1.email}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
              title={!step1.email ? "Geen e-mailadres bekend voor deze klant" : ""}
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              Mail klant
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={archiving || deleting}
              className={`rounded-md border px-3 py-1.5 text-sm font-medium transition disabled:opacity-50 ${
                data.isArchived
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {data.isArchived ? "Uit archief halen" : "Archiveren"}
            </button>
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              disabled={archiving || deleting}
              className="rounded-md border border-rose-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-700 transition hover:bg-rose-50 disabled:opacity-50"
            >
              Verwijderen
            </button>
          </div>
        </div>

        <div className="mt-5 grid gap-4 border-t border-gray-100 pt-5 sm:grid-cols-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Workflow-status
            </div>
            <p className="mb-2 text-[11px] text-gray-400">
              Waar in het proces deze aanvraag zit. Eén status tegelijk, lineair.
            </p>
            <BtwStatusMenu
              current={workflowStatus}
              options={statuses}
              onSelect={handleStatusChange}
            />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Toegewezen aan
            </div>
            <p className="mb-2 text-[11px] text-gray-400">
              Welke admin deze aanvraag oppakt.
            </p>
            <BtwAssigneeMenu
              current={assignee}
              admins={admins}
              currentUserId={userData?._id}
              onSelect={handleAssigneeChange}
            />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {data.billingMode === "partner_invoice"
                ? "Facturering & kwalificatie"
                : "Betaling & kwalificatie"}
            </div>
            <p className="mb-2 text-[11px] text-gray-400">
              {data.billingMode === "partner_invoice"
                ? "Of de partner-factuur is verstuurd / betaald, en of de aanvraag geschikt is."
                : "Of de klant heeft betaald, en of de aanvraag geschikt is."}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {data.billingMode === "partner_invoice" ? (
                <Badge
                  map={PARTNER_INVOICE_LABELS}
                  value={data.partnerInvoice?.status ?? "pending"}
                />
              ) : (
                <Badge map={STATUS_LABELS} value={data.status} />
              )}
              <Badge map={QUAL_LABELS} value={data.qualificationStatus} />
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-5 border-t border-gray-100 pt-5 sm:grid-cols-2">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Deadline
            </div>
            <p className="mb-2 text-[11px] text-gray-400">
              Trigger reminders naar de toegewezen admin op 7d / 4d / op-dag /
              wekelijks overdue.
            </p>
            <BtwDeadlinePicker
              value={data.deadlineAt}
              note={data.deadlineNote}
              schedule={data.reminderSchedule}
              onSave={handleDeadlineChange}
            />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Labels
            </div>
            <p className="mb-2 text-[11px] text-gray-400">
              Vrije tags die je orthogonaal aan de status zet. Meerdere tegelijk
              mogelijk (bv. &laquo;Wacht op klant&raquo; + &laquo;Spoed&raquo;).
            </p>
            <BtwLabelPicker
              selected={(data.labelIds ?? []).map((l) =>
                typeof l === "object" ? l._id : l,
              )}
              options={allLabels}
              onChange={handleLabelsChange}
              onLabelsChanged={async () => {
                const res = await adminFetchBtwLabels();
                setAllLabels(res?.data ?? res ?? []);
              }}
            />
          </div>
        </div>
      </div>

      <Section
        title="Aanvrager"
        editable
        isEditing={editingSection === "step1"}
        saving={savingSection}
        onEdit={() => startEdit("step1", step1)}
        onCancel={cancelEdit}
        onSave={() => saveSection("step1")}
      >
        {editingSection === "step1" ? (
          <>
            <EditRow
              label="Voorletters"
              value={editDraft.voorletters}
              onChange={(v) => setDraftField("voorletters", v)}
            />
            <EditRow
              label="Tussenvoegsel"
              value={editDraft.tussenvoegsel}
              onChange={(v) => setDraftField("tussenvoegsel", v)}
            />
            <EditRow
              label="Achternaam"
              value={editDraft.achternaam}
              onChange={(v) => setDraftField("achternaam", v)}
            />
            <EditRow
              label="Geslacht"
              value={editDraft.geslacht}
              onChange={(v) => setDraftField("geslacht", v)}
              options={[
                { value: "man", label: "Man" },
                { value: "vrouw", label: "Vrouw" },
                { value: "anders", label: "Anders" },
              ]}
            />
            <EditRow
              label="E-mail"
              type="email"
              value={editDraft.email}
              onChange={(v) => setDraftField("email", v)}
            />
            <EditRow
              label="Telefoon"
              type="tel"
              value={editDraft.telefoon}
              onChange={(v) => setDraftField("telefoon", v)}
            />
            <EditRow
              label="Postcode"
              value={editDraft.postcode}
              onChange={(v) => setDraftField("postcode", v.toUpperCase())}
            />
            <EditRow
              label="Huisnummer"
              value={editDraft.huisnummer}
              onChange={(v) => setDraftField("huisnummer", v)}
            />
            <EditRow
              label="Straatnaam"
              value={editDraft.straatnaam}
              onChange={(v) => setDraftField("straatnaam", v)}
            />
            <EditRow
              label="Plaatsnaam"
              value={editDraft.plaatsnaam}
              onChange={(v) => setDraftField("plaatsnaam", v)}
            />
          </>
        ) : (
          <>
            <Row label="Naam" value={fullName || "-"} />
            <Row label="Geslacht" value={genderLabel(step1.geslacht)} />
            <Row label="E-mail" value={step1.email} />
            <Row label="Telefoon" value={step1.telefoon} />
            <Row label="Adres" value={adres ?? "-"} />
          </>
        )}
      </Section>

      <Section
        title="Gegevens voor BTW-teruggave"
        editable
        isEditing={editingSection === "step3"}
        saving={savingSection}
        onEdit={() => startEdit("step3", step3)}
        onCancel={cancelEdit}
        onSave={() => saveSection("step3")}
      >
        {editingSection === "step3" ? (
          <>
            <EditRow
              label="BSN"
              value={editDraft.bsn}
              onChange={(v) => setDraftField("bsn", v.replace(/\D/g, ""))}
            />
            <EditRow
              label="Geboortedatum"
              type="date"
              value={(editDraft.geboortedatum ?? "").slice(0, 10)}
              onChange={(v) => setDraftField("geboortedatum", v)}
            />
            <EditRow
              label="IBAN"
              value={editDraft.iban}
              onChange={(v) => setDraftField("iban", v.toUpperCase().replace(/\s/g, ""))}
            />
            <EditRow
              label="Tenaamstelling IBAN"
              value={editDraft.ibanHolder}
              onChange={(v) => setDraftField("ibanHolder", v)}
            />
            <EditRow
              label="Factuurdatum"
              type="date"
              value={(editDraft.invoiceDate ?? "").slice(0, 10)}
              onChange={(v) => setDraftField("invoiceDate", v)}
            />
            <EditRow
              label="Installatiedatum"
              type="date"
              value={(editDraft.installationDate ?? "").slice(0, 10)}
              onChange={(v) => setDraftField("installationDate", v)}
            />
            <EditRow
              label="Capaciteit (kWh)"
              type="number"
              value={editDraft.batteryCapacityKwh}
              onChange={(v) => setDraftField("batteryCapacityKwh", v)}
            />
            <EditRow
              label="Aankoopbedrag (incl. btw)"
              type="number"
              value={editDraft.purchaseAmount}
              onChange={(v) => setDraftField("purchaseAmount", v)}
            />
            <EditRow
              label="Gebruik batterij"
              value={editDraft.batteryUsage}
              onChange={(v) => setDraftField("batteryUsage", v)}
              options={[
                { value: "trading", label: "Energie handel" },
                { value: "zelfgebruik", label: "Eigen verbruik" },
                { value: "beide", label: "Beide (eigen verbruik + handel)" },
                { value: "verhuurd", label: "Verhuurd aan energiemaatschappij" },
              ]}
            />
            <EditRow
              label="Dynamisch energiecontract"
              value={editDraft.dynamicEnergyContract}
              onChange={(v) => setDraftField("dynamicEnergyContract", v)}
              options={[
                { value: "ja", label: "Ja" },
                { value: "nee", label: "Nee" },
              ]}
            />
            {editDraft.dynamicEnergyContract === "ja" ? (
              <>
                <EditRow
                  label="Dyn. contract op naam aanvrager"
                  value={editDraft.dynamicContractInName}
                  onChange={(v) => setDraftField("dynamicContractInName", v)}
                  options={[
                    { value: "ja", label: "Ja" },
                    { value: "nee", label: "Nee" },
                  ]}
                />
                <EditRow
                  label="Energiecontract op naam aanvrager"
                  value={editDraft.energyContractInName}
                  onChange={(v) => setDraftField("energyContractInName", v)}
                  options={[
                    { value: "ja", label: "Ja" },
                    { value: "nee", label: "Nee" },
                  ]}
                />
                <EditRow
                  label="Afsluitdatum dyn. contract"
                  type="date"
                  value={(editDraft.dynamicContractSignDate ?? "").slice(0, 10)}
                  onChange={(v) => setDraftField("dynamicContractSignDate", v)}
                />
                <EditRow
                  label="Ingangsdatum contract"
                  type="date"
                  value={(editDraft.dynamicContractStartDate ?? "").slice(0, 10)}
                  onChange={(v) => setDraftField("dynamicContractStartDate", v)}
                />
                <p className="py-2 text-xs text-gray-500">
                  Ligt de ingangsdatum na de installatiedatum, dan telt de
                  afdracht over het eerste jaar vanaf de ingangsdatum.
                </p>
              </>
            ) : null}
            <EditRow
              label="Opmerking klant"
              value={editDraft.comments}
              onChange={(v) => setDraftField("comments", v)}
            />
          </>
        ) : (
          <>
            <Row label="BSN" value={step3.bsn} mono />
            <Row label="Geboortedatum" value={formatDate(step3.geboortedatum)} />
            <Row label="IBAN" value={step3.iban} mono />
            <Row label="Tenaamstelling IBAN" value={step3.ibanHolder} />
            <Row label="Factuurdatum" value={formatDate(step3.invoiceDate)} />
            <Row label="Installatiedatum" value={formatDate(step3.installationDate)} />
            <Row label="Capaciteit batterij" value={step3.batteryCapacityKwh ? `${step3.batteryCapacityKwh} kWh` : "-"} />
            <Row
              label="Aankoopbedrag (incl. btw)"
              value={
                step3.purchaseAmount
                  ? `€ ${Number(step3.purchaseAmount).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "-"
              }
            />
            <Row
              label="Gebruik batterij"
              value={batteryUsageLabel(step3.batteryUsage)}
            />
            <Row
              label="Dynamisch energiecontract"
              value={yesNoLabel(step3.dynamicEnergyContract)}
            />
            {step3.dynamicEnergyContract === "ja" ? (
              <>
                <Row
                  label="Dyn. contract op naam aanvrager"
                  value={yesNoLabel(step3.dynamicContractInName)}
                />
                <Row
                  label="Energiecontract op naam aanvrager"
                  value={yesNoLabel(step3.energyContractInName)}
                />
                <Row
                  label="Afsluitdatum dyn. contract"
                  value={formatDate(step3.dynamicContractSignDate)}
                />
                <Row
                  label="Ingangsdatum contract"
                  value={formatDate(step3.dynamicContractStartDate)}
                />
              </>
            ) : null}
          </>
        )}
      </Section>

      <Section title="Kwalificatie-antwoorden">
        {Object.entries(step2).length === 0 ? (
          <div className="py-2 text-sm text-gray-500">Geen antwoorden opgeslagen.</div>
        ) : (
          Object.entries(step2).map(([k, v]) => (
            <Row
              key={k}
              label={STEP2_LABELS[k] ?? k}
              value={step2ValueLabel(k, v)}
            />
          ))
        )}
        {data.qualificationFlag ? (
          <Row
            label="Vlag"
            value={QUALIFICATION_FLAG_LABELS[data.qualificationFlag] ?? data.qualificationFlag}
          />
        ) : null}
        {data.qualificationMessage ? (
          <Row label="Opmerking systeem" value={data.qualificationMessage} />
        ) : null}
      </Section>

      <Section title="Documenten">
        <div className="space-y-3 py-1">
          {/* Factuur */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Factuur batterij
                </p>
                {data.invoiceFile ? (
                  <a
                    href={data.invoiceFile.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 block truncate text-sm font-medium text-emerald-700 hover:underline"
                  >
                    {data.invoiceFile.originalName}{" "}
                    <span className="text-xs text-gray-500">
                      ({Math.round(data.invoiceFile.bytes / 1024)} KB)
                    </span>
                  </a>
                ) : (
                  <p className="mt-1 text-sm italic text-gray-500">
                    Niet geüpload
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                  {data.invoiceFile ? "Vervangen" : "Uploaden"}
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      handleReplaceInvoice(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                    disabled={fileBusy === "invoice"}
                  />
                </label>
                {data.invoiceFile ? (
                  <button
                    type="button"
                    onClick={handleDeleteInvoice}
                    disabled={fileBusy === "invoice"}
                    className="rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 shadow-sm hover:bg-rose-50 disabled:opacity-50"
                  >
                    Verwijderen
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {/* Bijlagen */}
          {(data.otherFiles ?? []).map((f, i) => (
            <div
              key={f.publicId ?? i}
              className="rounded-xl border border-gray-200 bg-white px-4 py-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Bijlage {i + 1}
                  </p>
                  <a
                    href={f.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 block truncate text-sm font-medium text-emerald-700 hover:underline"
                  >
                    {f.originalName}{" "}
                    <span className="text-xs text-gray-500">
                      ({Math.round(f.bytes / 1024)} KB)
                    </span>
                  </a>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteOtherFile(f.publicId)}
                  disabled={fileBusy === f.publicId}
                  className="rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 shadow-sm hover:bg-rose-50 disabled:opacity-50"
                >
                  Verwijderen
                </button>
              </div>
            </div>
          ))}

          {/* Voeg toe */}
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-600 hover:border-emerald-300 hover:text-emerald-700">
            {fileBusy === "addFile" ? (
              <span>Bezig met uploaden…</span>
            ) : (
              <>
                <span>+</span>
                <span>Bijlage toevoegen</span>
              </>
            )}
            <input
              type="file"
              className="hidden"
              onChange={(e) => {
                handleAddFile(e.target.files?.[0]);
                e.target.value = "";
              }}
              disabled={fileBusy === "addFile"}
            />
          </label>
        </div>
      </Section>

      {data.billingMode === "partner_invoice" ? (
        <Section title="Facturering (partner)">
          <Row
            label="Partner"
            value={
              data.partnerUserId?.bedrijfsnaam ??
              data.partnerUserId?.naam ??
              "-"
            }
          />
          <Row
            label="Factuur-status"
            value={
              <Badge
                map={PARTNER_INVOICE_LABELS}
                value={data.partnerInvoice?.status ?? "pending"}
              />
            }
          />
          {data.partnerInvoice?.invoiceId?.invoiceNumber ? (
            <Row
              label="Factuurnummer"
              value={data.partnerInvoice.invoiceId.invoiceNumber}
              mono
            />
          ) : null}
          {data.partnerInvoice?.invoicedAt ? (
            <Row label="Gefactureerd op" value={formatDateTime(data.partnerInvoice.invoicedAt)} />
          ) : null}
          {data.partnerInvoice?.paidAt ? (
            <Row label="Factuur betaald op" value={formatDateTime(data.partnerInvoice.paidAt)} />
          ) : null}
          <Row
            label="Klant betaalde"
            value="€ 0,00 (partner-flow)"
          />
        </Section>
      ) : null}

      <BtwBerekeningSection applicationId={id} reloadKey={reloadKey} />

      {/* Alleen zinvol bij een betaalde aanvraag: daarvoor is er nog niets aan
          te vragen bij de Belastingdienst. */}
      {data.status === "paid" ? (
      <Section title="Belastingdienst-formulier">
        <Row
          label="Opgaaf startende onderneming"
          value={
            <div>
              <button
                type="button"
                onClick={() => setBelastingdienstOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              >
                Formulier voorbereiden
              </button>
              <p className="mt-2 text-xs text-gray-500">
                Vult het originele ON0202-formulier van de Belastingdienst in met
                de gegevens uit deze aanvraag. Je controleert de waarden voordat
                de PDF gemaakt wordt.
              </p>
            </div>
          }
        />
        {data.belastingdienstForm?.file ? (
          <Row
            label="Opgeslagen formulier"
            value={
              <div>
                <button
                  type="button"
                  onClick={downloadBelastingdienstFormulier}
                  disabled={belastingdienstBusy}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  {belastingdienstBusy
                    ? "Ophalen…"
                    : data.belastingdienstForm.file.originalName ??
                      "Download PDF"}
                </button>
                <p className="mt-2 text-xs text-gray-500">
                  Gemaakt op{" "}
                  {formatDateTime(data.belastingdienstForm.generatedAt)}
                  {data.belastingdienstForm.generatedBy
                    ? ` door ${data.belastingdienstForm.generatedBy}`
                    : ""}
                  . Het bestand staat afgeschermd opgeslagen en is alleen via
                  deze knop op te halen.
                </p>
              </div>
            }
          />
        ) : null}
      </Section>
      ) : null}

      {data.machtiging ? (
        <Section title="Akkoord & handtekening">
          <Row
            label="Machtiging BTW-teruggave"
            value={
              data.machtiging.signedAt ? (
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                    Akkoord gegeven
                  </span>
                  op {formatDateTime(data.machtiging.signedAt)}
                </span>
              ) : (
                "-"
              )
            }
          />
          <Row
            label="Betaling factuur installateur"
            value={
              data.machtiging.invoicePaidConfirmed ? (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                    {data.machtiging.invoicePaidConfirmedBy === "admin"
                      ? "Bevestigd via ons"
                      : "Bevestigd door klant"}
                  </span>
                  {data.machtiging.invoicePaidConfirmedAt ? (
                    <span>
                      klant gaf op{" "}
                      {formatDateTime(data.machtiging.invoicePaidConfirmedAt)}{" "}
                      {data.machtiging.invoicePaidConfirmedBy === "admin"
                        ? "aan ons door dat de factuur volledig betaald is"
                        : "akkoord dat de factuur volledig betaald is"}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={undoInvoicePaid}
                    className="text-xs text-gray-400 underline hover:text-rose-600"
                  >
                    Ongedaan maken
                  </button>
                </span>
              ) : (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                    Nog niet bevestigd
                  </span>
                  <span className="text-gray-500">
                    de aanvraag wordt pas in behandeling genomen na betaling
                  </span>
                  <button
                    type="button"
                    onClick={confirmInvoicePaid}
                    disabled={invoicePaidBusy}
                    className="inline-flex items-center rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Markeer als betaald
                  </button>
                </span>
              )
            }
          />
          <Row
            label="Betaalbewijs"
            value={
              <span className="inline-flex flex-wrap items-center gap-3">
                {data.machtiging.invoicePaidProofFile?.url ? (
                  <a
                    href={data.machtiging.invoicePaidProofFile.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-700 underline hover:text-emerald-900"
                  >
                    {data.machtiging.invoicePaidProofFile.originalName ??
                      "Bekijk betaalbewijs"}
                  </a>
                ) : (
                  <span className="text-gray-400">Geen bewijs geupload</span>
                )}
                <label className="inline-flex cursor-pointer items-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                  {invoicePaidBusy
                    ? "Uploaden…"
                    : data.machtiging.invoicePaidProofFile?.url
                    ? "Vervangen"
                    : "Bewijs uploaden"}
                  <input
                    type="file"
                    className="hidden"
                    accept="application/pdf,image/*"
                    disabled={invoicePaidBusy}
                    onChange={(e) => {
                      uploadInvoicePaidProof(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </label>
              </span>
            }
          />
          {data.machtiging.signatureFile?.url ? (
            <Row
              label="Handtekening"
              value={
                <a
                  href={data.machtiging.signatureFile.url}
                  target="_blank"
                  rel="noreferrer"
                  title="Open handtekening in nieuw tabblad"
                >
                  <img
                    src={data.machtiging.signatureFile.url}
                    alt="Handtekening klant"
                    className="h-20 w-auto max-w-[260px] rounded-lg border border-gray-200 bg-white object-contain px-3 py-1.5"
                  />
                </a>
              }
            />
          ) : null}
          <Row
            label="Bewijs van machtiging"
            value={
              <button
                type="button"
                onClick={downloadMachtigingPdf}
                disabled={downloadingMachtiging}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
              >
                {downloadingMachtiging ? "Genereren…" : "Download PDF"}
              </button>
            }
          />
        </Section>
      ) : null}

      {data.billingMode !== "partner_invoice" ? (
        <Section title="Betaling">
          <Row label="Status" value={<Badge map={STATUS_LABELS} value={data.status} />} />
          <Row
            label="Bedrag"
            value={`€ ${(data.amountInclVat ?? 0).toFixed(2)} incl. btw (€ ${(data.amountExclVat ?? 0).toFixed(2)} excl. btw)`}
          />
          <Row label="Betaald op" value={formatDateTime(data.paidAt)} />
          <Row label="Stripe sessie" value={data.stripeSessionId} mono />
        </Section>
      ) : null}

      {(step3.comments || data.installateur) ? (
        <Section title="Notities & herkomst">
          {step3.comments ? (
            <Row label="Opmerking klant" value={step3.comments} />
          ) : null}
          {data.partnerUserId ? (
            <Row
              label="Installateur"
              value={
                data.partnerUserId.bedrijfsnaam ??
                data.partnerUserId.naam ??
                data.installateur
              }
            />
          ) : data.installateur ? (
            <Row label="Installateur" value={data.installateur} mono />
          ) : null}
        </Section>
      ) : null}

      <BtwActivityFeed
        activities={activities}
        onAddComment={handleAddComment}
        onEditComment={handleEditComment}
        onDeleteComment={handleDeleteComment}
        posting={postingComment}
        currentUserId={userData?._id}
        currentUserPhoto={userData?.profielFoto}
        statuses={statuses}
        admins={admins}
      />

      <SendCustomerEmailModal
        isOpen={emailOpen}
        applicationId={id}
        customerName={`${step1.voorletters ?? ""} ${step1.tussenvoegsel ?? ""} ${step1.achternaam ?? ""}`
          .replace(/\s+/g, " ")
          .trim() || "deze klant"}
        customerEmail={step1.email ?? ""}
        onClose={() => setEmailOpen(false)}
        onSent={() => refresh()}
      />

      <BelastingdienstFormModal
        isOpen={belastingdienstOpen}
        applicationId={id}
        onClose={() => setBelastingdienstOpen(false)}
        // Alleen verversen als er echt een formulier gemaakt is: dat zet een
        // regel in de activiteitenfeed. Bij annuleren is een refetch verspilling.
        onGenerated={() => refresh()}
      />

      <ConfirmModal
        isOpen={confirmOpen}
        title={data.isArchived ? "Uit archief halen?" : "Aanvraag archiveren?"}
        message={
          data.isArchived
            ? "Deze aanvraag wordt teruggezet naar het actieve overzicht."
            : "Deze aanvraag wordt gearchiveerd en verdwijnt uit het actieve overzicht. Je kunt 'm later terughalen via het Archief-tabblad."
        }
        confirmLabel={data.isArchived ? "Uit archief halen" : "Archiveren"}
        cancelLabel="Annuleren"
        busy={archiving}
        onConfirm={toggleArchive}
        onCancel={() => setConfirmOpen(false)}
      />

      <ConfirmModal
        isOpen={deleteOpen}
        variant="danger"
        title="Aanvraag definitief verwijderen?"
        message="Deze aanvraag verdwijnt volledig uit de admin. Niet meer terug te halen via de interface. Stripe-betaling blijft in het Stripe Dashboard beschikbaar."
        confirmLabel="Verwijder definitief"
        cancelLabel="Annuleren"
        busy={deleting}
        onConfirm={runDelete}
        onCancel={() => setDeleteOpen(false)}
      />

      {/* Generic action-confirm modal (file deletes, comment deletes etc.) */}
      <ConfirmModal
        isOpen={Boolean(actionModal)}
        variant={actionModal?.variant ?? "primary"}
        title={actionModal?.title ?? "Bevestigen?"}
        message={actionModal?.message ?? ""}
        confirmLabel={actionModal?.confirmLabel ?? "Bevestigen"}
        cancelLabel={actionModal?.cancelLabel ?? "Annuleren"}
        busy={actionBusy}
        onConfirm={runConfirm}
        onCancel={() => {
          if (!actionBusy) setActionModal(null);
        }}
      />
    </div>
  );
};

export default BtwBatterijAanvraagDetailsPage;
